import {
  Activity,
  ArrowRight,
  BadgeCheck,
  Check,
  CircleUserRound,
  Clock3,
  Coins,
  CreditCard,
  Download,
  History,
  ImageIcon,
  KeyRound,
  LoaderCircle,
  Moon,
  Palette,
  ReceiptText,
  RotateCcw,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Sun,
  Trash2,
  UserRoundCog
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { plans, formatPlanName } from '../data/plans';
import { tools } from '../data/tools';
import { SelectMenu } from '../components/SelectMenu';
import { useAuth } from '../hooks/useAuth';
import { getBillingPortal, getRecentJobs, type RecentJob } from '../lib/api';
import {
  clearNexhanceLocalData,
  getPreferences,
  savePreferences,
  type NexhancePreferences,
  type ThemePreference
} from '../lib/preferences';
import { Seo } from '../lib/seo';
import { FREE_ACCOUNT_CREDITS } from '../../shared/billing';

const sections = [
  { id: 'overview', label: 'Overview', description: 'Plan, credits and account summary', icon: Activity },
  { id: 'profile', label: 'Profile', description: 'Name and account identity', icon: CircleUserRound },
  { id: 'billing', label: 'Billing & plan', description: 'Subscription and checkout', icon: CreditCard },
  { id: 'history', label: 'Processing history', description: 'Recent image jobs', icon: History },
  { id: 'preferences', label: 'Preferences', description: 'Appearance and defaults', icon: SlidersHorizontal },
  { id: 'security', label: 'Security', description: 'Sign-in and sessions', icon: ShieldCheck },
  { id: 'privacy', label: 'Privacy', description: 'Browser data controls', icon: UserRoundCog }
] as const;

type SectionId = (typeof sections)[number]['id'];

function isSection(value: string | null): value is SectionId {
  return sections.some((section) => section.id === value);
}

function initials(value: string) {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  return (parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'N');
}

export function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const { user, profile, loading, profileLoading, profileError, session, refreshProfile } = useAuth();
  const requestedSection = params.get('section');
  const section: SectionId = isSection(requestedSection) ? requestedSection : 'overview';
  const checkoutStatus = params.get('checkout');
  const displayName = profile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'Nexhance AI member';
  const photoURL = profile?.photoURL || user?.photoURL || '';

  useEffect(() => {
    if (checkoutStatus !== 'success' || !user) return;
    const delays = [800, 2500, 6000];
    const timers = delays.map((delay) => window.setTimeout(() => void refreshProfile(), delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [checkoutStatus, refreshProfile, user]);

  if (loading || (user && profileLoading && !profile)) return <div className="page-loader">Loading account settings…</div>;
  if (!user) return <Navigate to="/auth?next=/settings" replace />;
  if (!profile) {
    return (
      <section className="page-section account-settings-page">
        <Seo title="Account Settings" description="Manage your Nexhance AI profile, plan, credits, image history, preferences and security." path="/settings" noindex />
        <div className="shell">
          <div className="settings-panel-card">
            <SettingsHeading eyebrow="Account connection" title="Your account is signed in, but the balance could not be loaded." description={profileError || 'The account service did not return a profile. Retry the secure account request.'} />
            <button type="button" className="button button-primary" disabled={profileLoading} onClick={() => void refreshProfile()}>
              <RotateCcw size={17} /> {profileLoading ? 'Retrying…' : 'Retry account balance'}
            </button>
          </div>
        </div>
      </section>
    );
  }

  function changeSection(next: SectionId) {
    const nextParams = new URLSearchParams(params);
    if (next === 'overview') nextParams.delete('section');
    else nextParams.set('section', next);
    nextParams.delete('checkout');
    setParams(nextParams, { replace: true });
  }


  return (
    <section className="page-section account-settings-page">
      <Seo title="Account Settings" description="Manage your Nexhance AI profile, plan, credits, image history, preferences and security." path="/settings" noindex />
      <div className="shell account-settings-shell">
        <aside className="settings-sidebar">
          <div className="settings-identity">
            <span className="account-avatar settings-avatar">
              {photoURL ? <img src={photoURL} alt="" referrerPolicy="no-referrer" /> : initials(displayName)}
            </span>
            <div><strong>{displayName}</strong><span>{user.email}</span></div>
          </div>
          <nav aria-label="Account settings">
            {sections.map((item) => {
              const Icon = item.icon;
              return (
                <button type="button" key={item.id} className={section === item.id ? 'active' : ''} onClick={() => changeSection(item.id)}>
                  <Icon size={18} />
                  <span><strong>{item.label}</strong><small>{item.description}</small></span>
                </button>
              );
            })}
          </nav>
        </aside>

        <div className="settings-content">
          {checkoutStatus === 'success' && (
            <div className="settings-notice success"><BadgeCheck size={19} /><div><strong>Payment completed.</strong><span>Your subscription will update after the billing webhook is verified.</span></div></div>
          )}
          {section === 'overview' && <OverviewSection profile={profile} jobsToken={session?.access_token} />}
          {section === 'profile' && <ProfileSection />}
          {section === 'billing' && <BillingSection planId={profile?.plan} credits={profile?.credits} allowance={profile?.monthlyCreditAllowance} status={profile?.subscriptionStatus} billingInterval={profile?.billingInterval} cancelAtPeriodEnd={profile?.cancelAtPeriodEnd} currentPeriodEnd={profile?.currentPeriodEnd} />}
          {section === 'history' && <HistorySection token={session?.access_token} />}
          {section === 'preferences' && <PreferencesSection />}
          {section === 'security' && <SecuritySection />}
          {section === 'privacy' && <PrivacySection />}
        </div>
      </div>
    </section>
  );
}

function SettingsHeading({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) {
  return <header className="settings-heading"><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{description}</p></header>;
}

function OverviewSection({ profile, jobsToken }: { profile: ReturnType<typeof useAuth>['profile']; jobsToken?: string }) {
  const [jobs, setJobs] = useState<RecentJob[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    getRecentJobs(jobsToken).then(setJobs).finally(() => setLoading(false));
  }, [jobsToken]);
  const currentPlan = formatPlanName(profile?.plan);

  return (
    <div>
      <SettingsHeading eyebrow="Account overview" title="Everything important, without a separate dashboard." description="Review your plan, available credits and recent processing from one focused account area." />
      <div className="settings-stat-grid">
        <article className="featured"><span><Coins size={20} /></span><small>Available credits</small><strong>{profile?.credits ?? '-'}</strong><p>{profile?.monthlyCreditAllowance ? `${profile.monthlyCreditAllowance.toLocaleString()} monthly plan allowance` : 'One-time free account balance'}</p><Link to="/pricing">Get more credits <ArrowRight size={15} /></Link></article>
        <article><span><Sparkles size={20} /></span><small>Current plan</small><strong>{currentPlan}</strong><p>{profile?.plan === 'free' ? 'All tools, adjustable detail enhancement and output up to 4×' : profile?.subscriptionStatus === 'on_hold' ? 'Payment update required to restore paid-plan access' : 'Subscription access is active'}</p><Link to="/settings?section=billing">View billing <ArrowRight size={15} /></Link></article>
        <article><span><History size={20} /></span><small>Recent jobs</small><strong>{loading ? '-' : jobs.length}</strong><p>Latest processing activity connected to this account</p><Link to="/settings?section=history">Open history <ArrowRight size={15} /></Link></article>
      </div>

      <div className="settings-panel-card overview-quick-start">
        <div className="settings-card-heading"><div><p className="eyebrow">Quick start</p><h2>Choose your next workflow</h2></div><Link to="/tools">All tools <ArrowRight size={16} /></Link></div>
        <div className="settings-tool-grid">
          {tools.slice(0, 6).map((tool) => {
            const Icon = tool.icon;
            return <Link key={tool.slug} to={`/tools/${tool.slug}`}><span className="tool-icon"><Icon size={18} /></span><div><strong>{tool.shortName}</strong><small>From {tool.creditCost} credits</small></div><ArrowRight size={16} /></Link>;
          })}
        </div>
      </div>

      <div className="settings-panel-card recent-summary">
        <div className="settings-card-heading"><div><p className="eyebrow">Recent activity</p><h2>Latest processing</h2></div><Link to="/settings?section=history">See all <ArrowRight size={16} /></Link></div>
        {loading ? <div className="settings-loading"><LoaderCircle size={20} /> Loading recent activity…</div> : jobs.length ? <JobList jobs={jobs.slice(0, 4)} /> : <EmptyHistory compact />}
      </div>
    </div>
  );
}

function ProfileSection() {
  const { user, profile, updateDisplayName } = useAuth();
  const initialName = profile?.displayName || user?.displayName || user?.email?.split('@')[0] || '';
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const providers = user?.providerData.map((provider) => provider.providerId).filter(Boolean) ?? [];

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true); setMessage(''); setError('');
    try {
      await updateDisplayName(name);
      setMessage('Your display name has been updated.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not update your profile.');
    } finally { setSaving(false); }
  }

  return (
    <div>
      <SettingsHeading eyebrow="Profile" title="Your account identity" description="Choose the name shown inside Nexhance AI and review the verified sign-in information connected to your account." />
      <div className="settings-panel-card profile-card">
        <div className="profile-card-top">
          <span className="account-avatar profile-avatar">{profile?.photoURL || user?.photoURL ? <img src={profile?.photoURL || user?.photoURL || ''} alt="" referrerPolicy="no-referrer" /> : initials(initialName)}</span>
          <div><strong>{initialName || 'Nexhance AI member'}</strong><span>{user?.email}</span><small>Profile photos are supplied by your sign-in provider.</small></div>
        </div>
        <form className="settings-form" onSubmit={submit}>
          <label><span>Display name</span><input value={name} onChange={(event) => setName(event.target.value)} minLength={2} maxLength={60} required /></label>
          <label><span>Email address</span><input value={user?.email ?? ''} readOnly disabled /><small>Your sign-in email is managed by Supabase Authentication.</small></label>
          <label><span>Connected sign-in</span><div className="provider-chips">{providers.map((provider) => <span key={provider}>{provider === 'google.com' ? 'Google' : provider === 'password' ? 'Email and password' : provider}</span>)}</div></label>
          {error && <div className="inline-error">{error}</div>}
          {message && <div className="inline-success">{message}</div>}
          <div className="settings-form-actions"><button className="button button-primary" disabled={saving}>{saving ? 'Saving…' : 'Save profile'}</button></div>
        </form>
      </div>
    </div>
  );
}

function BillingSection({
  planId,
  credits,
  allowance,
  status,
  billingInterval,
  cancelAtPeriodEnd,
  currentPeriodEnd
}: {
  planId?: string;
  credits?: number;
  allowance?: number;
  status?: string | null;
  billingInterval?: 'monthly' | 'yearly' | null;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string | null;
}) {
  const { session } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');
  const currentName = formatPlanName(planId);
  const activePlan = plans.find((plan) => plan.id === planId);
  const normalizedStatus = String(status ?? '').toLowerCase();
  const planIsUsable = Boolean(activePlan) && !['on_hold', 'cancelled', 'failed', 'expired'].includes(normalizedStatus);
  const statusLabel = !activePlan ? 'Free account' : normalizedStatus === 'on_hold' ? 'Payment update required' : normalizedStatus === 'cancelled' ? 'Cancelled' : normalizedStatus === 'expired' ? 'Expired' : normalizedStatus === 'failed' ? 'Payment failed' : normalizedStatus === 'pending' ? 'Pending activation' : cancelAtPeriodEnd ? 'Cancels at period end' : 'Active';
  const periodDate = currentPeriodEnd ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(currentPeriodEnd)) : null;
  const grantAmount = activePlan ? activePlan.credits * (billingInterval === 'yearly' ? 12 : 1) : 0;

  async function openPortal() {
    setPortalLoading(true);
    setPortalError('');
    try {
      const url = await getBillingPortal(session?.access_token);
      window.location.href = url;
    } catch (caught) {
      setPortalError(caught instanceof Error ? caught.message : 'Could not open the billing portal.');
      setPortalLoading(false);
    }
  }

  return (
    <div>
      <SettingsHeading eyebrow="Billing and plan" title="A clear view of your subscription" description="Review your current plan, credit grant, renewal status and secure subscription-management options." />
      <div className="billing-overview-grid">
        <div className="settings-panel-card billing-current-card">
          <div className="settings-card-heading"><div><p className="eyebrow">Current subscription</p><h2>{currentName}</h2></div><span className={`subscription-badge ${planIsUsable ? 'active' : ''}`}>{statusLabel}</span></div>
          <div className="billing-metrics"><div><small>Available now</small><strong>{credits ?? '-'} credits</strong></div><div><small>{billingInterval === 'yearly' ? 'Annual grant' : 'Monthly allowance'}</small><strong>{activePlan ? grantAmount.toLocaleString() : 'Not active'}</strong></div></div>
          <p>{activePlan ? billingInterval === 'yearly' ? `${grantAmount.toLocaleString()} credits are granted after each successful annual payment. The equivalent monthly allowance is ${allowance?.toLocaleString() ?? activePlan.credits.toLocaleString()}.` : `${allowance?.toLocaleString() ?? activePlan.credits.toLocaleString()} credits reset after each successful monthly renewal.` : `Signed-in new accounts receive ${FREE_ACCOUNT_CREDITS} one-time free credits. Choose a paid plan when you need the recurring allowance shown on the Pricing page.`}</p>
          {periodDate && activePlan && <p><strong>{cancelAtPeriodEnd ? 'Access scheduled until' : billingInterval === 'yearly' ? 'Annual renewal' : 'Next renewal'}:</strong> {periodDate}</p>}
          <div className="settings-form-actions">{activePlan && <button type="button" className="button button-primary" disabled={portalLoading} onClick={() => void openPortal()}>{portalLoading ? 'Opening portal…' : 'Manage subscription'}</button>}<Link to="/pricing" className={activePlan ? 'button button-ghost' : 'button button-primary'}>Compare plans</Link><Link to="/plans-and-credits" className="button button-ghost">How billing works</Link></div>
          {portalError && <div className="inline-error">{portalError}</div>}
        </div>
        <div className="settings-panel-card billing-security-card">
          <span className="settings-large-icon"><ShieldCheck size={24} /></span>
          <h2>Secure hosted payment</h2>
          <p>Card details are entered on the payment provider’s secure checkout, not stored in the Nexhance AI website or browser.</p>
          <ul><li><Check size={16} /> Tax calculation at checkout</li><li><Check size={16} /> Subscription receipt by email</li><li><Check size={16} /> Webhook-verified plan activation</li></ul>
        </div>
      </div>
      <div className="settings-panel-card billing-help-card">
        <div><ReceiptText size={21} /><span><strong>Invoices and subscription changes</strong><small>The customer portal becomes available after the first successful payment and supports payment details, invoices, cancellation and available plan changes.</small></span></div>
        <Link to="/refund-policy">Read refund policy <ArrowRight size={16} /></Link>
      </div>
    </div>
  );
}

function HistorySection({ token }: { token?: string }) {
  const [jobs, setJobs] = useState<RecentJob[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { getRecentJobs(token).then(setJobs).finally(() => setLoading(false)); }, [token]);
  return (
    <div>
      <SettingsHeading eyebrow="Processing history" title="Recent image jobs" description="Review completed, processing and failed runs. Temporary download links may expire after the retention window." />
      <div className="settings-panel-card history-settings-card">
        {loading ? <div className="settings-loading"><LoaderCircle size={22} /> Loading processing history…</div> : jobs.length ? <JobList jobs={jobs} /> : <EmptyHistory />}
      </div>
    </div>
  );
}

function JobList({ jobs }: { jobs: RecentJob[] }) {
  return <div className="job-list settings-job-list">{jobs.map((job) => {
    const tool = tools.find((item) => item.slug === job.tool);
    const Icon = tool?.icon ?? ImageIcon;
    const date = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(job.createdAt));
    const downloadable = Boolean(job.outputUrl) && job.status === 'completed' && Date.now() - new Date(job.createdAt).getTime() < 24 * 60 * 60 * 1000;
    return <article className="job-row" key={job.id}><span className="tool-icon"><Icon size={18} /></span><div className="job-main"><strong>{tool?.name ?? job.tool}</strong><span>{date} · {job.creditCost} credits</span></div><span className={`job-status ${job.status}`}>{job.status}</span>{downloadable ? <a className="icon-button" href={job.outputUrl!} target="_blank" rel="noreferrer" aria-label="Download result"><Download size={17} /></a> : <span className="job-action-placeholder" />}</article>;
  })}</div>;
}

function EmptyHistory({ compact = false }: { compact?: boolean }) {
  return <div className={`empty-history ${compact ? 'compact' : ''}`}><span><ImageIcon size={28} /></span><h3>No processed images yet</h3><p>Your finished and failed image jobs will appear here after the first processing run.</p><Link to="/tools/enhance" className="button button-primary">Enhance an image</Link></div>;
}

function PreferencesSection() {
  const [preferences, setPreferences] = useState<NexhancePreferences>(() => getPreferences());
  const [saved, setSaved] = useState(false);

  function update<K extends keyof NexhancePreferences>(key: K, value: NexhancePreferences[K]) {
    const next = { ...preferences, [key]: value };
    setPreferences(next);
    savePreferences(next);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1600);
  }

  const themeOptions: { value: ThemePreference; label: string; icon: LucideIcon }[] = [
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'system', label: 'System', icon: Palette }
  ];

  return (
    <div>
      <SettingsHeading eyebrow="Preferences" title="Make the studio feel familiar" description="Appearance and workflow defaults are stored in this browser and can be changed at any time." />
      <div className={`settings-toast ${saved ? 'visible' : ''}`} role="status" aria-live="polite">
        <div className="settings-notice success compact"><Check size={17} /><div><strong>Preference saved</strong></div></div>
      </div>
      <div className="settings-panel-card preference-card">
        <div className="preference-row"><div><strong>Appearance</strong><span>Choose the interface theme for this device.</span></div><div className="segmented-control">{themeOptions.map(({ value, label, icon: Icon }) => <button type="button" key={value} className={preferences.theme === value ? 'active' : ''} onClick={() => update('theme', value)}><Icon size={16} /> {label}</button>)}</div></div>
        <div className="preference-row"><div><strong>Default download format</strong><span>Used as the first option when a tool supports multiple formats.</span></div><SelectMenu ariaLabel="Default download format" value={preferences.downloadFormat} onChange={(next) => update('downloadFormat', next as NexhancePreferences['downloadFormat'])} choices={[{ value: 'jpg', label: 'JPG' }, { value: 'png', label: 'PNG' }, { value: 'webp', label: 'WebP' }]} /></div>
        <TogglePreference title="Remember tool settings" description="Keep the last selected strength, scale and finishing options in this browser." checked={preferences.rememberToolSettings} onChange={(value) => update('rememberToolSettings', value)} />
        <TogglePreference title="Reduce decorative animation" description="Keep functional progress indicators while reducing background motion and floating effects." checked={preferences.reducedAnimations} onChange={(value) => { update('reducedAnimations', value); document.documentElement.dataset.reducedMotion = value ? 'true' : 'false'; }} />
      </div>
    </div>
  );
}

function TogglePreference({ title, description, checked, onChange }: { title: string; description: string; checked: boolean; onChange: (value: boolean) => void }) {
  return <div className="preference-row"><div><strong>{title}</strong><span>{description}</span></div><button type="button" className={`switch ${checked ? 'active' : ''}`} onClick={() => onChange(!checked)} aria-pressed={checked} aria-label={`${title}: ${checked ? 'on' : 'off'}`}><span /></button></div>;
}

function SecuritySection() {
  const { user, sendPasswordReset, signOut } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const hasPassword = user?.providerData.some((provider) => provider.providerId === 'password');
  const providerNames = useMemo(() => user?.providerData.map((provider) => provider.providerId === 'google.com' ? 'Google' : provider.providerId === 'password' ? 'Email and password' : provider.providerId) ?? [], [user]);

  async function resetPassword() {
    setSending(true); setMessage(''); setError('');
    try { await sendPasswordReset(); setMessage('Password reset email sent. Check your inbox and spam folder.'); }
    catch (caught) { setError(caught instanceof Error ? caught.message : 'Could not send the reset email.'); }
    finally { setSending(false); }
  }

  async function closeSession() { await signOut(); navigate('/auth'); }

  return (
    <div>
      <SettingsHeading eyebrow="Security" title="Sign-in and session controls" description="Review how you access Nexhance AI and sign out safely from the current browser." />
      <div className="settings-panel-card security-settings-card">
        <div className="security-row"><span className="settings-large-icon"><KeyRound size={22} /></span><div><strong>Sign-in methods</strong><p>{providerNames.length ? providerNames.join(' · ') : 'Supabase Authentication'}</p></div><span className="verified-pill"><BadgeCheck size={15} /> Connected</span></div>
        <div className="security-row"><span className="settings-large-icon"><Clock3 size={22} /></span><div><strong>Current session</strong><p>This browser remains signed in using Supabase local persistence.</p></div><button type="button" className="button button-ghost" onClick={() => void closeSession()}>Sign out</button></div>
        {hasPassword && <div className="security-row"><span className="settings-large-icon"><RotateCcw size={22} /></span><div><strong>Reset password</strong><p>Send a secure reset link to {user?.email}.</p></div><button type="button" className="button button-ghost" disabled={sending} onClick={() => void resetPassword()}>{sending ? 'Sending…' : 'Send reset email'}</button></div>}
        {error && <div className="inline-error">{error}</div>}
        {message && <div className="inline-success">{message}</div>}
      </div>
    </div>
  );
}

function PrivacySection() {
  const [cleared, setCleared] = useState(false);
  function clearData() { clearNexhanceLocalData(); setCleared(true); }
  return (
    <div>
      <SettingsHeading eyebrow="Privacy" title="Control data stored in this browser" description="Manage local preferences and checkout drafts without deleting your Supabase account or subscription records." />
      <div className="settings-panel-card privacy-settings-card">
        <div className="privacy-callout"><ShieldCheck size={22} /><div><strong>What is stored locally</strong><p>Theme, workflow defaults and unfinished billing-form details may be saved in this browser. Authentication session storage is managed separately by Supabase.</p></div></div>
        <div className="danger-zone"><div><strong>Clear Nexhance AI local data</strong><span>Resets appearance, workflow preferences and unfinished checkout details. Your account, credits and processing history are not deleted.</span></div><button type="button" className="button button-danger-quiet" onClick={clearData}><Trash2 size={17} /> Clear local data</button></div>
        {cleared && <div className="inline-success">Local Nexhance AI preferences and checkout drafts were cleared.</div>}
        <div className="privacy-links"><Link to="/privacy">Privacy policy <ArrowRight size={15} /></Link><Link to="/cookies">Cookie and storage policy <ArrowRight size={15} /></Link></div>
      </div>
    </div>
  );
}
